"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Briefcase, Layers, UserCheck, CircleCheck as CheckCircle2, CircleX as XCircle, ArrowRight, TrendingUp, Target, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const brokenOptions = [{
  id: "agency",
  title: "Traditional SEO Agency",
  cost: "£2,500 – £6,000 / month",
  icon: Layers,
  flaw: "Delegates strategy to junior account managers juggling dozens of clients, missing the dedicated senior leadership and commercial direction of a fractional director."
}, {
  id: "task-freelancer",
  title: "Task-Based Freelancer",
  cost: "£350 – £650 / day",
  icon: Briefcase,
  flaw: "Tackles isolated tickets like keyword lists or metadata tweaks in a vacuum, with no broader accountability for qualified pipeline or revenue."
}, {
  id: "full-time",
  title: "Full-Time In-House Director",
  cost: "£80,000 – £120,000 / year",
  icon: UserCheck,
  flaw: "Adds significant overhead with national insurance, pensions, recruitment fees, and management friction that growing UK SMEs cannot justify."
}];
const fractionalPillars = [{
  title: "Senior Strategic Ownership",
  description: "Fractional leadership steering high-level search roadmaps, guiding execution across internal teams, and taking executive accountability for organic growth.",
  icon: Target
}, {
  title: "Revenue-Linked Search Strategy",
  description: "Every technical sprint, content architecture, and authority roadmap connects directly to commercial customer acquisition.",
  icon: TrendingUp
}, {
  title: "Embedded Team Integration",
  description: "I integrate into your Slack, product cycles, and editorial workflows to guide execution in real time.",
  icon: Sparkles
}];
export default function Fractional() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="fractionalseovalueproposition" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary">
            Fractional Strategic Leadership
          </Badge>
          <h2 className="font-sans text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Finally, a Fractional SEO Consultant Built for Growing UK Businesses
          </h2>
          <p className="mt-5 text-base text-muted-foreground sm:text-lg leading-relaxed">
            Growing companies need senior search leadership to expand organic traffic and generate qualified inbound enquiries, but hiring a full-time SEO Director at £80,000 to £120,000 a year creates heavy overhead. That is exactly where my fractional model fits.
          </p>
        </div>

        {/* The 3 Broken Alternatives */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-foreground">
              The three common options often fall short
            </h3>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {brokenOptions.map((opt, i) => {
            const Icon = opt.icon;
            return <motion.div key={opt.id} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card/60 shadow-xs flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <XCircle className="h-5 w-5 text-destructive/70" />
                      </div>
                      <CardTitle className="mt-4 text-lg font-semibold text-foreground">
                        {opt.title}
                      </CardTitle>
                      <p className="text-xs font-medium text-muted-foreground">
                        Estimated cost: {opt.cost}
                      </p>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {opt.flaw}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* The Fractional Solution Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mt-16 rounded-2xl border border-border bg-muted/40 p-8 sm:p-10 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <CheckCircle2 className="h-4 w-4" />
                <span>The Direct Bridge</span>
              </div>
              <h3 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                I built my fractional SEO consultancy to eliminate that frustration
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                As an independent freelance{" "}
                <Link to="/HomePage" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                  SEO Consultant
                </Link>
                , I bring deep technical SEO expertise, content architecture design, and commercially grounded organic strategy directly into your organisation. You gain senior strategic direction and practical execution without permanent salary liabilities or agency markups.
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                I work part-time as an embedded advisor. I bridge the gap between ad-hoc task freelancers and full-time executive hires—taking true strategic ownership and delivering measurable organic growth tied to your business objectives.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-4">
                {fractionalPillars.map((pillar, index) => {
                const PillarIcon = pillar.icon;
                return <div key={pillar.title} data-index={index} className="rounded-xl border border-border bg-card p-5 shadow-xs transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                          <PillarIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-foreground">
                            {pillar.title}
                          </h4>
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                            {pillar.description}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Closing Reinforcement */}
        <div className="mt-14 text-center">
          <p className="mx-auto max-w-2xl text-sm md:text-base text-muted-foreground">
            If you are navigating inconsistent organic performance, unhelpful agency reports, or search traffic disconnected from actual pipeline, this embedded fractional model provides the clarity and execution you need.
          </p>
        </div>
      </div>
    </section>;
}